import React, { useState, useCallback, useMemo } from "react";
import { useField, useFormikContext } from "formik";
import { Input as MuiInput } from "@material-ui/core";

const PostCodeInput = ({
  name = "excludePostCodes",
  label = "Exclude Post Codes",
  color = "#71BF44",
  background = "rgba(0, 0, 0, 0.04)",
  disabled = false,
}) => {
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField(name);
  const [newPostCode, setNewPostCode] = useState("");
  const [error, setError] = useState("");

  const postCodes = useMemo(() => field.value || [], [field.value]);

  const isValidPostCode = useCallback(
    (code) => {
      if (!code) return false;
      if (!/^\d{4}$/.test(code)) return false;
      if (postCodes.includes(code)) return false;
      return true;
    },
    [postCodes]
  );

  const updateErrorMessage = useCallback(
    (code) => {
      if (!code) {
        setError("");
        return false;
      }
      if (!/^\d{4}$/.test(code)) {
        setError("Post code must be 4 digits");
        return false;
      }
      if (postCodes.includes(code)) {
        setError("Post code already exists");
        return false;
      }
      setError("");
      return true;
    },
    [postCodes]
  );

  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setNewPostCode(value);

    if (error) setError("");
  };

  const handleAddPostCode = () => {
    if (updateErrorMessage(newPostCode)) {
      const updatedPostCodes = [...postCodes, newPostCode];
      setFieldValue(name, updatedPostCodes);
      setNewPostCode("");
    }
  };

  const handleRemovePostCode = (codeToRemove) => {
    const updatedPostCodes = postCodes.filter((code) => code !== codeToRemove);
    setFieldValue(name, updatedPostCodes);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (isValidPostCode(newPostCode)) {
        handleAddPostCode();
      }
    }
  };

  const wrapperStyle = {
    backgroundColor: background,
    padding: "8px 15px 8px",
    marginBottom: "10px",
    borderBottom: `1px solid ${color}`,
    borderRadius: "4px 4px 0 0",
  };

  const labelStyle = {
    fontSize: "12px",
    color: color,
    letterSpacing: "0.4px",
    marginBottom: "8px",
  };

  const tagContainerStyle = {
    display: "inline",
    flexWrap: "wrap",
    marginBottom: "10px",
    minHeight: "30px",
  };

  const tagStyle = {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    padding: "2px 8px",
    borderRadius: "16px",
    margin: "0 8px 8px 0",
    fontSize: "14px",
  };

  const inputContainerStyle = {
    display: "inline-flex",
    alignItems: "center",
    background: "rgba(232, 245, 225, 1)",
    borderRadius: "16px",
    padding: "2px 8px",
  };

  const errorStyle = {
    color: "red",
    fontSize: "12px",
    marginTop: "4px",
  };

  return (
    <div>
      <div style={wrapperStyle}>
        <div style={labelStyle}>{label}</div>

        <div style={tagContainerStyle}>
          {postCodes.map((code) => (
            <div key={code} style={tagStyle}>
              {code}
              <button
                type="button"
                onClick={() => handleRemovePostCode(code)}
                disabled={disabled}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: "0 0 0 4px",
                  color: "#999",
                  fontSize: "16px",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <div style={inputContainerStyle}>
          <MuiInput
            disableUnderline
            value={newPostCode}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            disabled={disabled}
            placeholder="1234"
            style={{ width: "50px" }}
            inputProps={{
              maxLength: 4,
              style: { padding: "4px 0" },
              "data-1p-ignore": true,
            }}
          />

          <button
            type="button"
            onClick={handleAddPostCode}
            disabled={disabled || !isValidPostCode(newPostCode)}
            style={{
              border: "none",
              background: "none",
              cursor: isValidPostCode(newPostCode) ? "pointer" : "default",
              color: isValidPostCode(newPostCode) ? color : "#999",
              fontSize: "24px",
              padding: "0 0 0 8px",
              marginTop: "-2px",
            }}
          >
            +
          </button>
        </div>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      {meta.touched && meta.error && <div style={errorStyle}>{meta.error}</div>}
    </div>
  );
};

export default PostCodeInput;
